import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { queryKeys } from '~/api/queryKeys';

import { getBatchCoverage, startCoverageRecompute } from '../services/catalog-operations.service';
import { isBatchCoverageEmpty } from '../utils/coverage-transforms';

import { pollCoverageRecomputeUntilSettled } from './use-coverage-recompute';

/** How often `pollWhileEmpty` callers re-ask for the async coverage rows. */
export const BATCH_COVERAGE_POLL_MS = 5000;

/** How long to keep asking before treating an empty payload as genuinely
 *  empty. Coverage is written by an async callback shortly after the run
 *  COMPLETES, so the first responses come back empty; without a ceiling a run
 *  that never produces coverage would shimmer forever. Mirrors the feed-audit
 *  insights budget. */
export const BATCH_COVERAGE_POLL_BUDGET_MS = 120_000;

/**
 * Fetches the Enrichment Coverage tab's signal cards for one batch.
 * `workflowId` is `BatchDetail.batchId` (the human-readable id), not the job
 * uuid — the same identifier `useBatchInsights` takes.
 */
export function useBatchCoverage(workflowId: string, enabled = true, pollWhileEmpty = false) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchCoverage(workflowId),
    queryFn: () => getBatchCoverage(workflowId),
    enabled: enabled && Boolean(workflowId),
    refetchInterval: (query) =>
      pollWhileEmpty && isBatchCoverageEmpty(query.state.data) ? BATCH_COVERAGE_POLL_MS : false,
  });
}

interface BatchCoverageResult {
  coverage: ReturnType<typeof useBatchCoverage>['data'];
  isLoading: boolean;
  /** True while rows may still arrive — what callers shimmer on. Goes false
   *  once rows land, the auto-recompute settles, or the budget is spent, so a
   *  run that never produces coverage ends in its empty state. */
  isAwaitingRows: boolean;
  /** True while the auto-recompute is in flight. Callers show a distinct
   *  "recomputing" state for it rather than the plain awaiting shimmer. */
  isRecomputing: boolean;
}

/** Empty coverage responses to tolerate before auto-kicking a recompute. The
 *  rows are written by an async callback after the run completes; one empty poll
 *  is enough of a signal that they aren't coming on their own, so recompute
 *  rather than shimmer against nothing. */
export const BATCH_COVERAGE_EMPTY_POLLS_BEFORE_RECOMPUTE = 1;

/** `polling` → waiting on the async coverage callback; `recomputing` → an
 *  auto-recompute is in flight; `settled` → give up polling and let the coverage
 *  read decide the UI (rows or empty state). */
type AutoRecomputePhase = 'polling' | 'recomputing' | 'settled';

interface AutoRecomputeState {
  /** Workflow this state tracks; a different id resets everything. */
  id: string;
  /** Empty responses counted so far. */
  emptyPolls: number;
  /** `dataUpdatedAt` of the last counted response — dedupes re-renders so one
   *  fetch is only ever counted once. */
  countedAt: number;
  phase: AutoRecomputePhase;
}

function initialAutoRecomputeState(id: string): AutoRecomputeState {
  return { id, emptyPolls: 0, countedAt: 0, phase: 'polling' };
}

/**
 * `useBatchCoverage` with a bounded poll **and** empty-coverage self-healing.
 *
 * Coverage rows are written by an async callback just after a run completes, so
 * the tab often mounts against an empty payload. This reads coverage once; if it
 * comes back empty it silently fires a recompute (the same POST-then-poll the
 * manual button runs), surfacing `isRecomputing` so the caller can show a
 * progress state, then re-reads coverage. That stops the shimmer-forever case
 * where nothing ever triggers the rows to be produced.
 *
 * Silent by design — this is automatic recovery, not a user action, so it raises
 * no toasts (the manual button keeps those). A missing `connectorCode` can't be
 * recomputed, so that case falls back to the plain bounded poll until the budget
 * settles it.
 */
export function useBatchCoverageWithAutoRecompute(
  workflowId: string,
  connectorCode: string,
  enabled = true,
): BatchCoverageResult {
  const queryClient = useQueryClient();
  const canRecompute = Boolean(connectorCode);

  // Per-workflow orchestration, reconciled at render so a new batch id starts
  // clean without waiting an extra effect tick.
  const [state, setState] = useState<AutoRecomputeState>(() =>
    initialAutoRecomputeState(workflowId),
  );
  if (state.id !== workflowId) setState(initialAutoRecomputeState(workflowId));
  const active = state.id === workflowId ? state : initialAutoRecomputeState(workflowId);

  // Ultimate ceiling: a run that never yields rows and can't be recomputed
  // settles here instead of shimmering for the rest of the session.
  useEffect(() => {
    if (!workflowId || !enabled) return;
    const timer = setTimeout(() => {
      setState((s) => (s.id === workflowId ? { ...s, phase: 'settled' } : s));
    }, BATCH_COVERAGE_POLL_BUDGET_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [workflowId, enabled]);

  const query = useBatchCoverage(workflowId, enabled, active.phase !== 'settled');
  const isEmpty = isBatchCoverageEmpty(query.data);

  // Count each empty fetch once. At the threshold, move to `recomputing` when a
  // connector is available; without one, stay in `polling` so the budget ceiling
  // (not the threshold) decides when to give up — preserving the old behaviour.
  useEffect(() => {
    if (!enabled || !workflowId) return;
    if (!query.isSuccess || !isEmpty) return;
    setState((s) => {
      if (s.id !== workflowId || s.phase !== 'polling') return s;
      if (query.dataUpdatedAt === s.countedAt) return s;
      const emptyPolls = s.emptyPolls + 1;
      const reachedThreshold =
        canRecompute && emptyPolls >= BATCH_COVERAGE_EMPTY_POLLS_BEFORE_RECOMPUTE;
      return {
        ...s,
        emptyPolls,
        countedAt: query.dataUpdatedAt,
        phase: reachedThreshold ? 'recomputing' : 'polling',
      };
    });
  }, [enabled, workflowId, query.isSuccess, query.dataUpdatedAt, isEmpty, canRecompute]);

  // Fire the recompute exactly once, on entering `recomputing`: POST, poll the
  // status to a terminal state, then re-read coverage and settle. Whatever the
  // outcome we settle — rows landing shows data, still-empty shows the empty
  // state — so there is no re-trigger loop.
  useEffect(() => {
    if (active.phase !== 'recomputing') return;
    void (async () => {
      try {
        const run = await startCoverageRecompute(workflowId, connectorCode);
        await pollCoverageRecomputeUntilSettled(run.runId);
      } catch {
        // Swallow: a failed/rejected recompute just settles into the empty
        // state, same as a run that legitimately produced no coverage.
      } finally {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.catalogOperations.batchCoverage(workflowId),
        });
        // The identity guard keeps this safe if the batch changed mid-run: the
        // settle only lands while this is still the active workflow's state.
        setState((s) => (s.id === workflowId ? { ...s, phase: 'settled' } : s));
      }
    })();
  }, [active.phase, workflowId, connectorCode, queryClient]);

  return {
    coverage: query.data,
    isLoading: query.isLoading,
    isAwaitingRows: Boolean(workflowId) && enabled && isEmpty && active.phase !== 'settled',
    isRecomputing: active.phase === 'recomputing',
  };
}
